import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Image as ImageIcon, Users, ArrowUpRight, Link2, Unlink, Plus, LogOut } from 'lucide-react';
import Modal from '../../components/Modal';
import TypeaheadSelect from '../../components/TypeaheadSelect';
import ZoomControl, { useZoom, clampZoom } from '../../components/ZoomControl';
import { getAccountOrg, setContactManager, setContactOrgPosition, setContactOrgPositions, setContactPlacement } from '../../api';
import OrgCanvas, { layoutNodes, connectorPath, NODE_W, NODE_H } from './OrgCanvas';
import './OrgCanvas.css';

// ─── Build generic org nodes from the account's PLACED contacts ─────────────
function buildOrgNodes(org, collapsedKeys) {
  const placed = (org?.contacts || []).filter(c => c.on_org_chart);
  const ids = new Set(placed.map(c => c.id));
  return placed.map(c => ({
    key: `c${c.id}`,
    type: 'contact',
    id: c.id,
    name: c.name,
    sublabel: c.job_title || null,
    // Manager is a parent only if they're also placed on the chart.
    parentKey: (c.manager_contact_id != null && ids.has(c.manager_contact_id)) ? `c${c.manager_contact_id}` : null,
    savedX: c.org_x,
    savedY: c.org_y,
    collapsed: collapsedKeys.has(`c${c.id}`),
    raw: c,
  }));
}

// ─── PNG export (shares OrgCanvas layout + connector geometry) ───────────────
function escapeXml(str) {
  return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}
function truncate(str, max) { const s = String(str || ''); return s.length > max ? s.slice(0, max - 1) + '…' : s; }

function downloadOrgPng(nodes, accountName) {
  const { nodes: list, byKey, positions, width, height } = layoutNodes(nodes);
  const F = 'font-family="Segoe UI, Arial, sans-serif"';
  let edges = '';
  list.forEach(n => {
    if (!n.parentKey || !byKey.has(n.parentKey)) return;
    const pp = positions.get(n.parentKey); const cp = positions.get(n.key);
    if (!pp || !cp) return; // skip collapsed/hidden
    edges += `<path d="${connectorPath(pp, cp)}" stroke="#94a3b8" stroke-width="2" fill="none"/>`;
  });
  let cards = '';
  list.forEach(n => {
    const p = positions.get(n.key);
    if (!p) return; // collapsed/hidden
    cards += `<rect x="${p.x}" y="${p.y}" width="${NODE_W}" height="${NODE_H}" rx="8" fill="#ffffff" stroke="#9aa6b4" stroke-width="1.5"/>`;
    cards += `<text x="${p.x + 14}" y="${p.y + 30}" ${F} font-size="13.5" font-weight="700" fill="#1f2933">${escapeXml(truncate(n.name, 24))}</text>`;
    if (n.sublabel) cards += `<text x="${p.x + 14}" y="${p.y + 50}" ${F} font-size="11" fill="#64748b">${escapeXml(truncate(n.sublabel, 28))}</text>`;
  });
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><rect width="${width}" height="${height}" fill="#f8fafc"/>${edges}${cards}</svg>`;
  const img = new Image();
  img.onload = () => {
    const scale = 2;
    const cv = document.createElement('canvas');
    cv.width = Math.round(width * scale); cv.height = Math.round(height * scale);
    const ctx = cv.getContext('2d'); ctx.scale(scale, scale); ctx.drawImage(img, 0, 0);
    cv.toBlob(blob => {
      if (!blob) { alert('Could not generate image.'); return; }
      const safe = (accountName || 'account').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = `org-chart-${safe}.png`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
    }, 'image/png');
  };
  img.onerror = () => alert('Could not render the chart image.');
  img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

// ─── Tab component ──────────────────────────────────────────────────────────
function AccountOrgChartTab({ account, canDo }) {
  const navigate = useNavigate();
  // Window zoom for a chart that has outgrown the card; remembered per browser.
  const [zoom, setZoom] = useZoom('crm_zoom_org_chart');
  const boardRef = useRef(null);
  const fitZoom = () => {
    // The drawing's own width is its inline style; offsetWidth would report
    // the canvas stretched to the window. Fit only ever shrinks.
    const board = boardRef.current; const canvas = board?.firstElementChild;
    const drawingWidth = canvas ? (parseFloat(canvas.style.width) || canvas.offsetWidth) : 0;
    if (!board || !drawingWidth) return;
    setZoom(clampZoom(Math.min(1, (board.clientWidth - 2) / drawingWidth)));
  };
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [menuNode, setMenuNode] = useState(null);
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
  const [drawerSearch, setDrawerSearch] = useState('');

  const canEdit = canDo('contacts', 'edit');

  const load = useCallback(async () => {
    try {
      const res = await getAccountOrg(account.id);
      setOrg(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load org chart');
    } finally {
      setLoading(false);
    }
  }, [account.id]);

  useEffect(() => { load(); }, [load]);

  const nodes = useMemo(() => buildOrgNodes(org, collapsedKeys), [org, collapsedKeys]);

  // Contacts not yet placed on the chart → left panel.
  const unassigned = useMemo(() => {
    const list = (org?.contacts || []).filter(c => !c.on_org_chart);
    const q = drawerSearch.trim().toLowerCase();
    return q ? list.filter(c => (c.name || '').toLowerCase().includes(q)) : list;
  }, [org, drawerSearch]);
  const placedCount = useMemo(() => (org?.contacts || []).filter(c => c.on_org_chart).length, [org]);

  const withError = useCallback(async (fn) => {
    try { setError(''); await fn(); }
    catch (err) { setError(err.response?.data?.error || 'Action failed'); }
  }, []);

  const onMoveNode = useCallback((node, x, y) => withError(async () => {
    const res = await setContactOrgPosition(node.id, x, y);
    setOrg(res.data);
  }), [withError]);

  // Dragging a manager moves its whole subtree — persist all moved contacts.
  const onMoveNodes = useCallback((moves) => withError(async () => {
    const res = await setContactOrgPositions(moves.map(m => ({ id: m.node.id, x: m.x, y: m.y })));
    setOrg(res.data);
  }), [withError]);

  // Connect handle = "make the target report to this person": parent → child.
  const onConnect = useCallback((parentNode, childNode) => withError(async () => {
    const res = await setContactManager(childNode.id, parentNode.id);
    setOrg(res.data);
  }), [withError]);

  const onNodeClick = useCallback((node) => setMenuNode(node), []);

  const onToggleCollapse = useCallback((node) => {
    setCollapsedKeys(prev => {
      const next = new Set(prev);
      if (next.has(node.key)) next.delete(node.key); else next.add(node.key);
      return next;
    });
  }, []);

  const placeContact = useCallback((id) => withError(async () => {
    const res = await setContactPlacement(id, true);
    setOrg(res.data);
  }), [withError]);

  const removeContact = useCallback((id) => withError(async () => {
    const res = await setContactPlacement(id, false);
    setOrg(res.data);
    setMenuNode(null);
  }), [withError]);

  // Manager picker: any other placed contact (server guards cycles).
  const managerOptionsFor = useCallback((contactId) =>
    (org?.contacts || []).filter(c => c.on_org_chart && c.id !== contactId).map(c => ({ id: c.id, name: c.name })),
    [org]
  );

  if (loading) return <div className="am-loading">Loading org chart…</div>;

  return (
    <div className="am-tab">
      <div className="am-toolbar">
        <div className="am-toolbar-info">
          <Users size={16} />
          <span>{placedCount} on chart · {(org?.contacts || []).length - placedCount} unassigned — drag to arrange, use a card's + handle to set reporting lines.</span>
        </div>
        <div className="am-toolbar-actions">
          <ZoomControl zoom={zoom} onChange={setZoom} onFit={fitZoom} label="Zoom the chart" />
          <button className="btn btn-secondary btn-sm" onClick={() => downloadOrgPng(nodes, account.name)} disabled={!nodes.length}>
            <ImageIcon size={15} /> Export PNG
          </button>
          <button className="btn btn-icon btn-sm" title="Refresh" onClick={load}>
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="am-org-body">
        {/* Unassigned contacts panel */}
        <div className="am-drawer">
          <div className="am-drawer-head">Unassigned ({unassigned.length})</div>
          <input
            className="form-input am-drawer-search"
            placeholder="Search contacts…"
            value={drawerSearch}
            onChange={(e) => setDrawerSearch(e.target.value)}
          />
          {unassigned.length === 0 ? (
            <div className="am-drawer-empty">
              {(org?.contacts || []).length === 0 ? 'No contacts on this account.' : 'All contacts are on the chart.'}
            </div>
          ) : unassigned.map(c => (
            <div key={c.id} className="am-drawer-row">
              <div className="am-drawer-row-info">
                <div className="am-drawer-name" title={c.name}>{c.name}</div>
                {c.job_title && <div className="am-drawer-title" title={c.job_title}>{c.job_title}</div>}
              </div>
              {canEdit && (
                <button className="btn btn-primary btn-sm am-drawer-place" onClick={() => placeContact(c.id)}>
                  <Plus size={13} /> Place
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="am-canvas-scroll" ref={boardRef}>
          <OrgCanvas
            zoom={zoom}
            nodes={nodes}
            canEdit={canEdit}
            onMoveNode={onMoveNode}
            onMoveNodes={onMoveNodes}
            dragMovesSubtree
            onNodeClick={onNodeClick}
            onConnect={onConnect}
            onToggleCollapse={onToggleCollapse}
            connectTitle="Set a report: draw to a contact to make them report to this person"
            emptyMessage="No contacts placed yet — add people from the left panel."
          />
        </div>
      </div>

      <OrgNodeMenu
        node={menuNode}
        onClose={() => setMenuNode(null)}
        canEdit={canEdit}
        navigate={navigate}
        managerOptionsFor={managerOptionsFor}
        onSetManager={(id, managerId) => withError(async () => {
          const res = await setContactManager(id, managerId);
          setOrg(res.data); setMenuNode(null);
        })}
        onRemove={removeContact}
      />
    </div>
  );
}

// ─── Node action modal ──────────────────────────────────────────────────────
function OrgNodeMenu({ node, onClose, canEdit, navigate, managerOptionsFor, onSetManager, onRemove }) {
  const [managerId, setManagerId] = useState('');
  const [managerName, setManagerName] = useState('');
  useEffect(() => { setManagerId(''); setManagerName(''); }, [node]);
  if (!node) return null;
  const hasManager = node.raw?.manager_contact_id != null;
  return (
    <Modal isOpen={!!node} onClose={onClose} title={node.name} size="sm">
      <div className="modal-body am-menu">
        <button className="am-menu-item" onClick={() => { onClose(); navigate(`/contacts/${node.id}`); }}>
          <ArrowUpRight size={16} /> Open contact
        </button>
        {canEdit && (
          <>
            <div className="am-menu-section">
              <label>Reports to</label>
              <TypeaheadSelect
                id="org-set-manager"
                placeholder="Search contacts…"
                items={managerOptionsFor(node.id)}
                value={managerId}
                displayValue={managerName}
                onChange={(id, name) => { setManagerId(id); setManagerName(name); }}
              />
              <button className="btn btn-primary btn-sm am-menu-apply" disabled={!managerId}
                onClick={() => onSetManager(node.id, managerId)}>
                <Link2 size={14} /> Set manager
              </button>
            </div>
            {hasManager && (
              <button className="am-menu-detach" onClick={() => onSetManager(node.id, null)}>
                <Unlink size={14} /> Remove manager (move to top level)
              </button>
            )}
            <button className="am-menu-item am-menu-danger" onClick={() => onRemove(node.id)}>
              <LogOut size={16} /> Remove from chart
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}

export default AccountOrgChartTab;
